/**
 * API client para os endpoints REST do module_lab.
 *
 * Cobre o que a integração com a GameSession precisa: listar aulas
 * disponíveis, criar/ativar/encerrar sessão. JWT é injetado via apiService.
 */

import { apiService } from '@/services/api'

export interface LabGameSummary {
  slug: string
  title: string
  subject?: string | null
  version: number
}

export interface LabSessionResponse {
  id: string
  game_id: string
  game_slug: string | null
  table_id: string
  master_user_id: string
  current_slide_index: number
  current_activity_id: string | null
  status: 'idle' | 'live' | 'ended'
  started_at: string | null
  ended_at: string | null
}

export const labApi = {
  listGames: async (): Promise<LabGameSummary[]> => {
    return await apiService.request<LabGameSummary[]>('/modules/module_lab/games')
  },

  createSession: async (tableId: string, gameSlug: string): Promise<LabSessionResponse> => {
    return await apiService.request<LabSessionResponse>('/modules/module_lab/sessions', {
      method: 'POST',
      body: JSON.stringify({ table_id: tableId, game_slug: gameSlug }),
    })
  },

  startSession: async (sessionId: string): Promise<LabSessionResponse> => {
    return await apiService.request<LabSessionResponse>(
      `/modules/module_lab/sessions/${encodeURIComponent(sessionId)}/start`,
      { method: 'POST' },
    )
  },

  endSession: async (sessionId: string): Promise<LabSessionResponse> => {
    return await apiService.request<LabSessionResponse>(
      `/modules/module_lab/sessions/${encodeURIComponent(sessionId)}/end`,
      { method: 'POST' },
    )
  },

  getActiveForTable: async (tableId: string): Promise<LabSessionResponse | null> => {
    return await apiService.request<LabSessionResponse | null>(
      `/modules/module_lab/tables/${encodeURIComponent(tableId)}/active-session`,
    )
  },

  /**
   * Atribui equipes a um slide colaborativo (PR-3 do SPEC-ATLAS-V2).
   * Body: { slide_index, assignments: { <teamId>: { payload, membership_ids } } }
   * Retorna contagem de memberships notificados.
   */
  assignTeams: async (
    sessionId: string,
    body: {
      slide_index: number
      assignments: Record<string, { payload: Record<string, unknown>; membership_ids: string[] }>
    },
  ): Promise<{ ok: boolean; slide_index: number; team_ids: string[]; memberships_notified: number }> => {
    return await apiService.request(
      `/modules/module_lab/sessions/${encodeURIComponent(sessionId)}/assign-teams`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    )
  },
}
